import * as Snackbar from '@synergy/shared/snackbar/data-access';
import { ApiResult, PaginatedResponse } from '@synergy/api';
import { Dispatch } from 'redux';
import { DocumentStatus, DocumentType, ObjectWithId, ReceiptAlert } from '@synergy/models';
import { PoolNotification } from '@synergy/shared/snackbar/data-access';
import { downloadFile } from '@synergy/utils';
import { paloIsRefused } from '@synergy/components-palo';
import { uploadFilesSet } from '@synergy/data-access';

import { Api } from '../../api';
import { AppState } from '../../store/models/app-state';
import { LockedPartner, noticeLockedPartners } from '../../common/utils/locked-partner-notice';
import { PurchaseOrder } from './models/purchaseOrder';
import { resourceMissing } from '../../common/reducers/resource-missing-reducer';

export const REQUEST_PURCHASE_ORDERS = 'REQUEST_PURCHASE_ORDERS';
export const RECEIVE_PURCHASE_ORDERS = 'RECEIVE_PURCHASE_ORDERS';
export const RESET_PURCHASE_ORDERS_STATE = 'RESET_PURCHASE_ORDERS_STATE';
export const REQUEST_PURCHASE_ORDER = 'REQUEST_PURCHASE_ORDER';
export const RECEIVE_PURCHASE_ORDER = 'RECEIVE_PURCHASE_ORDER';
export const DELETE_PURCHASE_ORDER = 'DELETE_PURCHASE_ORDER';
export const RESET_PURCHASE_ORDER_STATE = 'RESET_PURCHASE_ORDER_STATE';
export const CREATE_PURCHASE_ORDER = 'CREATE_PURCHASE_ORDER';
export const UPDATE_PURCHASE_ORDER = 'UPDATE_PURCHASE_ORDER';
export const DUPLICATE_PURCHASE_ORDER = 'DUPLICATE_PURCHASE_ORDER';
export const REQUEST_PURCHASE_ORDER_START = 'REQUEST_PURCHASE_ORDER_START';
export const REQUEST_PURCHASE_ORDER_END = 'REQUEST_PURCHASE_ORDER_END';
export const UPDATE_PURCHASE_ORDER_ALERT = 'UPDATE_PURCHASE_ORDER_ALERT';
export const COMPLETE_PURCHASE_ORDER = 'COMPLETE_PURCHASE_ORDER';

export const requestStart = () => {
    return {
        type: REQUEST_PURCHASE_ORDER_START,
    };
};

export const requestEnd = () => {
    return {
        type: REQUEST_PURCHASE_ORDER_END,
    };
};

export const requestPurchaseOrders = () => {
    return {
        type: REQUEST_PURCHASE_ORDERS,
    };
};

export const resetPurchaseOrdersState = () => {
    return {
        type: RESET_PURCHASE_ORDERS_STATE,
    };
};

export const receivePurchaseOrders = (purchaseOrders: PaginatedResponse<PurchaseOrder>) => {
    return {
        type: RECEIVE_PURCHASE_ORDERS,
        purchaseOrders,
    };
};

export const requestPurchaseOrder = () => {
    return {
        type: REQUEST_PURCHASE_ORDER,
    };
};

export const createPurchaseOrder = () => {
    return {
        type: CREATE_PURCHASE_ORDER,
    };
};

export const updatePurchaseOrder = () => {
    return {
        type: UPDATE_PURCHASE_ORDER,
    };
};

export const deletePurchaseOrderAction = (purchaseOrderId: number) => {
    return {
        type: DELETE_PURCHASE_ORDER,
        purchaseOrderId,
    };
};

export const resetPurchaseOrderState = () => {
    return {
        type: RESET_PURCHASE_ORDER_STATE,
    };
};

export const receivePurchaseOrder = (entity: PurchaseOrder) => {
    return {
        type: RECEIVE_PURCHASE_ORDER,
        entity,
    };
};

const purchaseOrderCompleted = (status: DocumentStatus, entity: PurchaseOrder) => {
    return {
        type: COMPLETE_PURCHASE_ORDER,
        status,
        entity,
    };
};

const purchaseOrderAlertUpdated = (alert?: ReceiptAlert) => {
    return {
        type: UPDATE_PURCHASE_ORDER_ALERT,
        alert,
    };
};

export const fetchPurchaseOrders = (searchParams: URLSearchParams) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch(requestStart());
        dispatch(requestPurchaseOrders());

        const response = await api.purchaseOrders.cget(searchParams);
        if (response.success) {
            dispatch(receivePurchaseOrders(response.result));
            dispatch(requestEnd());
        } else {
            dispatch(requestEnd());
            // The list says a refused filter in its own body, with the cause
            // and a way out; the toast would say it again without either.
            if (!paloIsRefused(searchParams.toString())) {
                dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
            }
        }
    };
};

export const fetchPurchaseOrder = (id: number) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch(requestPurchaseOrder());

        const response = await api.purchaseOrders.get(id);
        if (response.success) {
            dispatch(receivePurchaseOrder(response.result));
            // The files as the server has them, only when it sends the
            // document: set from every new copy of it, an upload just made
            // was overwritten by the files it was loaded with (R-193, R-194).
            dispatch(uploadFilesSet(response.result.media ?? []));
        } else if (404 === response.errorCode) {
            // Not a fault to report in passing: the address names a record
            // that does not exist, and the mask says so instead of standing empty.
            dispatch(requestEnd());
            dispatch(resourceMissing());
        } else {
            dispatch(requestEnd());
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const duplicatePurchaseOrder = (id: number, partners: (LockedPartner | undefined)[] = []) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: DUPLICATE_PURCHASE_ORDER });

        const response = await api.purchaseOrders.copyPurchaseOrder(id);
        dispatch(requestEnd());

        if (response.success) {
            noticeLockedPartners(dispatch, partners);
            dispatch(receivePurchaseOrder(response.result));
            dispatch(uploadFilesSet(response.result.media ?? []));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

const toPurchaseOrderRequest = (purchaseOrder: Partial<PurchaseOrder>) => {
    const { paymentTerm, classifications, ...purchaseOrderRequestBase } = purchaseOrder;
    const classificationIds = classifications?.map(classification => classification.id) || [];

    return {
        ...purchaseOrderRequestBase,
        classificationIds,
        paymentTermId: paymentTerm?.id,
        functionalAssetId: purchaseOrderRequestBase.functionalAsset?.id ?? null,
        functionalAsset: undefined,
    };
};

/**
 * Says that the save did not happen.
 *
 * A save that never starts — because the forms handed nothing up — looked
 * exactly like one that worked: no request, no message, the mask standing
 * still (#2210, R-95).
 */
export const reportPurchaseOrderSaveFailed = () => {
    return (dispatch: Dispatch) => {
        dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.purchaseOrderSavedError')));
    };
};

export const createOrUpdatePurchaseOrder = (purchaseOrder: Partial<PurchaseOrder>) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        let response: ApiResult<PurchaseOrder>;

        const purchaseOrderRequest = toPurchaseOrderRequest(purchaseOrder);

        if (purchaseOrder.id) {
            dispatch(updatePurchaseOrder());
            response = await api.purchaseOrders.patch(purchaseOrder.id, purchaseOrderRequest);
        } else {
            dispatch(createPurchaseOrder());
            response = await api.purchaseOrders.post(purchaseOrderRequest);
        }

        if (response.success) {
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.purchaseOrderSavedSuccess')));
            dispatch(receivePurchaseOrder(response.result));
            dispatch(uploadFilesSet(response.result.media ?? []));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.purchaseOrderSavedError')));
        }
    };
};

/**
 * Cancelling writes the whole document back, so it has to start from the whole
 * document. A row of the overview carries a slim projection — no surcharge items,
 * no functional asset, no supplier offer number — and sending that back sets each
 * of those to nothing.
 */
export const cancelPurchaseOrder = (id: number) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch(updatePurchaseOrder());

        const current = await api.purchaseOrders.get(id);

        if (!current.success) {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.purchaseOrderSavedError')));

            return;
        }

        const response = await api.purchaseOrders.patch(id, {
            ...toPurchaseOrderRequest(current.result),
            status: DocumentStatus.Canceled,
        });

        if (response.success) {
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.purchaseOrderSavedSuccess')));
            dispatch(receivePurchaseOrder(response.result));
            dispatch(uploadFilesSet(response.result.media ?? []));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.purchaseOrderSavedError')));
        }
    };
};

export const updateReceiptAlert = (id: number, alert: ReceiptAlert) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        const response = await api.receiptAlert.updateAlert(DocumentType.PurchaseOrder, id, alert);
        const isNew = !getState().purchaseOrders.entity?.receiptAlert;
        if (response.success) {
            dispatch(purchaseOrderAlertUpdated(alert));
            dispatch(
                Snackbar.enqueue(
                    PoolNotification.success(
                        isNew ? 'components.receiptAlert.snackbar.added' : 'components.receiptAlert.snackbar.changed'
                    )
                )
            );
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('components.receiptAlert.snackbar.error')));
        }
    };
};

export const deleteReceiptAlert = (id: number) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        const response = await api.receiptAlert.deleteAlert(DocumentType.PurchaseOrder, id);
        if (response.success) {
            dispatch(purchaseOrderAlertUpdated());
            dispatch(Snackbar.enqueue(PoolNotification.success('components.receiptAlert.snackbar.deleted')));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('components.receiptAlert.snackbar.error')));
        }
    };
};

export const deletePurchaseOrder = (id: number) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch(deletePurchaseOrderAction(id));
        const response = await api.purchaseOrders.delete(id);
        if (response.success === false) {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

/** Completing was asked for while the order has no position (#2331 R-256). */
export const notifyCompleteNeedsPosition = () =>
    Snackbar.enqueue(PoolNotification.error('app.purchaseOrders.detail.completeNeedsPosition'));

export const completePurchaseOrder = (purchaseOrder: Partial<PurchaseOrder> & ObjectWithId) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        // An empty list here is never meant: an order is not completed without
        // a position, and sent along it would remove every saved one first
        // (#2331 R-256).
        if (0 === (purchaseOrder.purchaseOrderItems ?? []).length) {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.purchaseOrders.detail.completeNeedsPosition')));

            return;
        }

        dispatch(updatePurchaseOrder());

        const { paymentTerm, classifications, ...purchaseOrderRequestBase } = purchaseOrder;
        const classificationIds = classifications?.map(classification => classification.id) || [];
        const purchaseOrderRequest = {
            ...purchaseOrderRequestBase,
            classificationIds,
            paymentTermId: paymentTerm?.id,
        };

        const updateResponse = await api.purchaseOrders.patch(purchaseOrder.id, purchaseOrderRequest);

        if (updateResponse.success === false) {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));

            return;
        }

        // What the server kept, not what the form held.
        if (0 === (updateResponse.result.purchaseOrderItems ?? []).length) {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.purchaseOrders.detail.completeNeedsPosition')));

            return;
        }

        const response = await api.purchaseOrders.complete(purchaseOrder.id);

        if (response.success) {
            dispatch(purchaseOrderCompleted(response.result.status, updateResponse.result));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const exportPurchaseOrders = (filename: string, searchParams: URLSearchParams) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        const response = await api.purchaseOrders.export(searchParams);
        if (response.success) {
            downloadFile(response.result, filename);
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};
